import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type ThemeName = 'dark' | 'light' | 'glass';

interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  success: string;
  warning: string;
  danger: string;
  background: string;
  backgroundSecondary: string;
  card: string;
  cardHover: string;
  border: string;
  borderLight: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  shadow: string;
  shadowLg: string;
}

interface ThemeStyles {
  card: React.CSSProperties;
  button: React.CSSProperties;
  input: React.CSSProperties;
  badge: React.CSSProperties;
  transition: string;
}

interface ThemeContextType {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  colors: ThemeColors;
  styles: ThemeStyles;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Color Palettes
const THEMES: Record<ThemeName, ThemeColors> = {
  dark: {
    primary: '#00d9ff',
    secondary: '#0066ff',
    accent: '#00d9ff',
    success: '#00ff88',
    warning: '#ffaa00',
    danger: '#ff0055',
    background: '#0a0a0f',
    backgroundSecondary: '#12121a',
    card: '#15151f',
    cardHover: '#1a1a28',
    border: '#2a2a3e',
    borderLight: '#1f1f2e',
    text: '#ffffff',
    textSecondary: '#b3b3cc',
    textMuted: '#808099',
    shadow: 'rgba(0, 217, 255, 0.1)',
    shadowLg: 'rgba(0, 102, 255, 0.15)',
  },
  light: {
    primary: '#4f46e5',
    secondary: '#7c3aed',
    accent: '#06b6d4',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    background: '#f9fafb',
    backgroundSecondary: '#f3f4f6',
    card: '#ffffff',
    cardHover: '#f9fafb',
    border: '#e5e7eb',
    borderLight: '#f0f1f3',
    text: '#111827',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    shadow: 'rgba(79, 70, 229, 0.08)',
    shadowLg: 'rgba(79, 70, 229, 0.12)',
  },
  glass: {
    primary: '#a78bfa',
    secondary: '#f0abfc',
    accent: '#c084fc',
    success: '#86efac',
    warning: '#fbbf24',
    danger: '#f87171',
    background: '#0f0a1a',
    backgroundSecondary: '#1a0f2e',
    card: 'rgba(30, 20, 60, 0.4)',
    cardHover: 'rgba(40, 30, 80, 0.5)',
    border: 'rgba(167, 139, 250, 0.2)',
    borderLight: 'rgba(167, 139, 250, 0.1)',
    text: '#f3e8ff',
    textSecondary: '#d8b4fe',
    textMuted: '#a78bfa',
    shadow: 'rgba(167, 139, 250, 0.2)',
    shadowLg: 'rgba(167, 139, 250, 0.3)',
  },
};

const getThemeStyles = (colors: ThemeColors, themeName: ThemeName): ThemeStyles => {
  const baseTransition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

  if (themeName === 'glass') {
    return {
      card: {
        backgroundColor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        backdropFilter: 'blur(10px)',
        boxShadow: `0 8px 32px ${colors.shadow}`,
        transition: baseTransition,
      },
      button: {
        backgroundColor: colors.primary,
        color: '#000',
        border: 'none',
        borderRadius: '12px',
        padding: '10px 20px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: baseTransition,
        boxShadow: `0 4px 15px ${colors.shadow}`,
      },
      input: {
        backgroundColor: colors.card,
        borderColor: colors.border,
        color: colors.text,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        padding: '12px 16px',
        transition: baseTransition,
        backdropFilter: 'blur(10px)',
      },
      badge: {
        backgroundColor: `${colors.primary}20`,
        color: colors.primary,
        borderRadius: '20px',
        padding: '6px 12px',
        fontSize: '12px',
        fontWeight: '600',
        border: `1px solid ${colors.border}`,
      },
      transition: baseTransition,
    };
  }

  if (themeName === 'light') {
    return {
      card: {
        backgroundColor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: '12px',
        boxShadow: `0 1px 3px ${colors.shadow}`,
        transition: baseTransition,
      },
      button: {
        backgroundColor: colors.primary,
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        padding: '10px 20px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: baseTransition,
        boxShadow: `0 2px 8px ${colors.shadow}`,
      },
      input: {
        backgroundColor: colors.card,
        borderColor: colors.border,
        color: colors.text,
        borderRadius: '8px',
        border: `1px solid ${colors.border}`,
        padding: '12px 16px',
        transition: baseTransition,
      },
      badge: {
        backgroundColor: `${colors.primary}15`,
        color: colors.primary,
        borderRadius: '16px',
        padding: '6px 12px',
        fontSize: '12px',
        fontWeight: '600',
        border: `1px solid ${colors.border}`,
      },
      transition: baseTransition,
    };
  }

  // Dark theme
  return {
    card: {
      backgroundColor: colors.card,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      boxShadow: `0 8px 24px ${colors.shadow}`,
      transition: baseTransition,
    },
    button: {
      backgroundColor: colors.primary,
      color: '#000000',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: baseTransition,
      boxShadow: `0 0 20px ${colors.shadow}`,
    },
    input: {
      backgroundColor: colors.backgroundSecondary,
      borderColor: colors.border,
      color: colors.text,
      borderRadius: '8px',
      border: `1px solid ${colors.border}`,
      padding: '12px 16px',
      transition: baseTransition,
    },
    badge: {
      backgroundColor: `${colors.primary}20`,
      color: colors.accent,
      borderRadius: '16px',
      padding: '6px 12px',
      fontSize: '12px',
      fontWeight: '600',
      border: `1px solid ${colors.border}`,
    },
    transition: baseTransition,
  };
};

const setCSSVariables = (colors: ThemeColors) => {
  const root = document.documentElement;
  Object.entries(colors).forEach(([key, value]) => {
    root.style.setProperty(`--theme-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, value);
  });
};

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeName;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'dark',
}) => {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme') as ThemeName | null;
      return saved || defaultTheme;
    }
    return defaultTheme;
  });

  const colors = THEMES[theme];
  const styles = getThemeStyles(colors, theme);

  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.backgroundColor = colors.background;
    document.documentElement.style.color = colors.text;
    setCSSVariables(colors);
  }, [theme, colors]);

  const setTheme = (newTheme: ThemeName) => {
    setThemeState(newTheme);
  };

  const value: ThemeContextType = {
    theme,
    setTheme,
    colors,
    styles,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeToggleProps {
  className?: string;
  style?: React.CSSProperties;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = '', style }) => {
  const { theme, setTheme } = useTheme();
  const { colors } = useTheme();

  const themes: { name: ThemeName; icon: string; label: string }[] = [
    { name: 'dark', icon: '🌙', label: 'Dark' },
    { name: 'light', icon: '☀️', label: 'Light' },
    { name: 'glass', icon: '💎', label: 'Glass' },
  ];

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        gap: '8px',
        padding: '4px',
        backgroundColor: colors.backgroundSecondary,
        borderRadius: '8px',
        border: `1px solid ${colors.border}`,
        ...style,
      }}
    >
      {themes.map(({ name, icon, label }) => (
        <button
          key={name}
          onClick={() => setTheme(name)}
          title={`Switch to ${label} theme`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: theme === name ? colors.primary : 'transparent',
            color: theme === name ? '#000' : colors.textSecondary,
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (theme !== name) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                colors.borderLight;
            }
          }}
          onMouseLeave={(e) => {
            if (theme !== name) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            }
          }}
        >
          <span>{icon}</span>
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
};

export { ThemeContext };
export type { ThemeColors, ThemeStyles };
